import { useEffect, useState, useMemo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  HStack,
  VStack,
  Badge,
  Spinner,
  Link,
} from '@chakra-ui/react'
import { Copy, Edit, PhoneCall, X } from 'lucide-react'
import { getTicketRequestSourceEmail } from '@/lib/services/ticketRequestService'
import type { TicketRequest } from '@/types/ticketRequest'
import {
  RequestDetailSections,
  buildProgressSteps,
  buildRequestDetailSections,
} from '@/components/client/RequestDetailSections'

function getReceivedEmailId(row: TicketRequest | null): number | null {
  if (!row?.raw_payload || typeof row.raw_payload !== 'object') return null
  const id = row.raw_payload.received_email_id
  if (typeof id === 'number' && id > 0) return id
  if (typeof id === 'string' && id.trim() !== '') {
    const n = Number(id)
    return Number.isFinite(n) && n > 0 ? n : null
  }
  return null
}

function shouldLoadSourceEmail(row: TicketRequest | null): boolean {
  if (!row) return false
  if (getReceivedEmailId(row) != null) return true
  const extRef = row.external_reference_id
  if (typeof extRef === 'string' && extRef.trim() !== '') return true
  if (row.request_source === 'email' || row.request_channel === 'email') return true
  return false
}

function resolveStoredRequestDetails(row: TicketRequest): string | null {
  const raw = typeof row.raw_message === 'string' ? row.raw_message.trim() : ''
  const desc = typeof row.description === 'string' ? row.description.trim() : ''
  if (raw.startsWith('Sample raw input for ')) {
    return desc || null
  }
  if (raw) return raw
  return desc || null
}

type Props = {
  isOpen: boolean
  onClose: () => void
  row: TicketRequest | null
  isLoading?: boolean
  onCallOut?: (row: Record<string, unknown>) => void
  showCallOut?: boolean
  onEdit?: (row: TicketRequest) => void
  onDuplicate?: (row: TicketRequest) => void
}

export function TicketRequestDetailModal({
  isOpen,
  onClose,
  row,
  isLoading = false,
  onCallOut,
  showCallOut = true,
  onEdit,
  onDuplicate,
}: Props) {
  const [liveEmail, setLiveEmail] = useState<{ subject: string; body: string } | null>(null)
  const [isFetchingEmail, setIsFetchingEmail] = useState(false)

  useEffect(() => {
    if (!isOpen || !row?.id) {
      setLiveEmail(null)
      setIsFetchingEmail(false)
      return
    }

    if (!shouldLoadSourceEmail(row)) {
      setLiveEmail(null)
      setIsFetchingEmail(false)
      return
    }

    let cancelled = false
    setLiveEmail(null)
    setIsFetchingEmail(true)

    getTicketRequestSourceEmail(row.id)
      .then((data) => {
        if (cancelled) return
        setLiveEmail({
          subject: data.subject ?? '',
          body: data.body ?? '',
        })
      })
      .catch(() => {
        if (cancelled) return
        setLiveEmail(null)
      })
      .finally(() => {
        if (!cancelled) setIsFetchingEmail(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, row])

  const requestNumber = row?.request_number ?? '–'
  const statusLabel =
    row?.ticket_status_label ?? (row?.ticket_status != null ? String(row.ticket_status) : '–')

  const extractedFields = row?.extracted_fields ?? null
  const subjectRaw =
    (liveEmail?.subject && liveEmail.subject.trim() !== '' ? liveEmail.subject : undefined) ??
    row?.email_subject ??
    (extractedFields?.email_subject as string | undefined) ??
    (extractedFields?.subject_normalized as string | undefined)
  const subject = subjectRaw != null && String(subjectRaw).trim() !== '' ? String(subjectRaw).trim() : null

  const storedDetails = row ? resolveStoredRequestDetails(row) : null
  const detailBody =
    liveEmail?.body && liveEmail.body.trim() !== '' ? liveEmail.body : storedDetails

  const canEditDraft = row != null && !row.submitted_at

  const progressSteps = useMemo(() => (row ? buildProgressSteps(row) : []), [row])

  const sections = useMemo(() => {
    if (!row) return []
    return buildRequestDetailSections(row, { detailBody, isFetchingEmail })
  }, [row, detailBody, isFetchingEmail])

  const parentTicketLink =
    row?.parent_ticket_number != null ? (
      <Link
        fontFamily="mono"
        fontSize="sm"
        color="blue.600"
        fontWeight="600"
        href="#"
        onClick={(e) => e.preventDefault()}
        title="Linked ticket"
      >
        {row.parent_ticket_number}
      </Link>
    ) : undefined

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader py={4} px={5} pr={12}>
          <HStack spacing={3} flexWrap="wrap" align="center">
            <Text fontSize="lg" fontWeight="semibold">
              Request Details
            </Text>
            {row ? (
              <>
                <Text fontSize="sm" color="gray.600" fontWeight="500">
                  {requestNumber}
                </Text>
                <Badge colorScheme="blue" variant="outline" fontSize="xs">
                  {statusLabel}
                </Badge>
              </>
            ) : null}
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={3} right={3} />
        <ModalBody px={5} pb={4}>
          {isLoading ? (
            <HStack justify="center" py={10}>
              <Spinner size="md" />
              <Text fontSize="sm" color="gray.500">
                Loading request…
              </Text>
            </HStack>
          ) : row ? (
            <RequestDetailSections
              row={row}
              sections={sections}
              progressSteps={progressSteps}
              parentTicketLink={parentTicketLink}
              subject={subject}
            />
          ) : (
            <Text fontSize="sm" color="gray.500">
              Request not found.
            </Text>
          )}
        </ModalBody>
        <ModalFooter borderTopWidth="1px" borderColor="gray.100" py={3} px={5}>
          <HStack spacing={2} justify="space-between" w="100%">
            <HStack spacing={2}>
              {canEditDraft && onEdit ? (
                <Button
                  leftIcon={<Edit size={16} />}
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(row!)}
                >
                  Edit
                </Button>
              ) : null}
              {row && onDuplicate ? (
                <Button
                  leftIcon={<Copy size={16} />}
                  variant="outline"
                  size="sm"
                  onClick={() => onDuplicate(row)}
                >
                  Duplicate as new
                </Button>
              ) : null}
            </HStack>
            <HStack spacing={2}>
              {showCallOut && onCallOut && row ? (
                <VStack spacing={0} align="flex-end">
                  <Button
                    leftIcon={<PhoneCall size={18} />}
                    colorScheme="green"
                    size="md"
                    onClick={() => onCallOut(row as unknown as Record<string, unknown>)}
                  >
                    Call out
                  </Button>
                  <Text fontSize="xs" color="gray.500" mt={1} textAlign="right" maxW="220px">
                    ALICE will call to collect missing request details
                  </Text>
                </VStack>
              ) : null}
              <Button size="sm" variant="ghost" onClick={onClose} leftIcon={<X size={16} />}>
                Close
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
